<script setup lang="ts">

import { ref} from 'vue';

const teaser = ref();
const theRest = ref();

function hideMost(_: Event) {
  teaser.value.removeAttribute('hidden');
  theRest.value.setAttribute('hidden', null);
}

function showMost(evt: Event) {
  teaser.value.setAttribute('hidden', null);
  theRest.value.removeAttribute('hidden');
  evt.stopPropagation();
}

</script>

<template>
  <div
    class="explainer-card"
    @click="hideMost" >
    <div>
      <h3>What is this thing?</h3>
      This web tool allows you to explore your financial future in England and
      Wales in the United Kingdom.
      It models various tax, growth, asset types and income flows.
      <span ref="teaser"
           class="teaser"
           @click="showMost">
        Click here for more...
      </span>
    </div>
    <div ref="theRest" class="the-rest" hidden>
      <div>
        <h3>Key concepts</h3>
        <ul>
          <li>
            All amounts are valued in today's GBP: this means we don't have to
            think about inflation. No-one knows what £1000 in twenty years will
            buy you. Will it be enough to buy a pint of milk? Another way to
            describe this is to say that all cashflows and valuations are
            discounted to
            <span class="acronym" title="Net Present Value">NPV</span>.
          </li>
          <li>
            Growth rates you choose should be the "real" rates: they should be
            smaller than the gross rates by the rate of inflation.
            For example: if the gross growth rate of your ISA is expected to be
            6%, but inflation runs at 2.5%, then the real growth rate is 3.5%.
          </li>
          <li>
            The model is not interested in your earnings, tax, or spending
            before your declared retirement age, only your saving into your
            retirement asset pools, and their growth. Pre-retirement savings
            rates set in the strategy section below allow you to model your
            asset pools' growth until you retire (and beyond).
          </li>
          <li>
            Dividend and capital gain taxations' effects are dealt with by the
            difference between the two ("real") growth rates you specify: the
            growth of your unprotected asset pool is lower because their income
            and capital growth are taxed. The asset pool growth flows
            calculated are thus net of both tax types where appropriate.
          </li>
        </ul>
      </div>
      <div>
        <h3>Model Features and Capabilities</h3>
        <ul>
          <li>
            <b>Tiered income tax on pension withdrawals.</b> As of 2024-12,
            national insurance doesn't come into play, because it's not
            applicable to post retirement income/earnings, and the model
            ignores taxes before retirement except their implicit effects on
            the net growth rate of your unprotected savings/assets. The model
            assumes that tax thresholds will rise in line with inflation (e.g.
            future tax amounts discounted to today will be the same as if they
            had been calculated today, although this is not happening at the
            moment, and so real average tax rates are climbing for many
            incomes)
          </li>
          <li>
            <b>Tracking and impact of your tax free pension allowance.</b> As
            of some time in 2024, your 25% "lump sum" allowance is capped at
            £268,275. As all quantities are discounted to today's money, the
            calculations assume that this limit will rise in line with
            inflation.
          </li>
          <li>
            <b>Iterative drawdown</b> from your three asset pools to achieve
            specified net (post-tax) retirement income.
          </li>
          <li>
            <b>Optional progressive transfer</b> of unprrotected and pension
            assets to your ISA pool after retirement to improve tax efficiency.
          </li>
          <li>
            <b>CSV downloads of plotted data</b> - right click on the legend
            for the plot you're interested in, and select download from the
            context menu that should pop up.
          </li>
          <li>
            Extensive <b>hover text explanations</b>: point your mouse at
            things, and wait a couple of seconds to see if some text pops up.
          </li>
          <li>
            Less extensive <b>explanations for mobile browsers</b>, accessed by
            clicking on stuff.
          </li>
          <li>
            Try typing k at the end of a money amount number as a <b>shortcut
            for thousands</b>, M (capital M only) for millions. If you want G
            to work for billions, you are probably using the wrong tool!
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
